/**
 * Step — AI effect (optional). Public catalog with title search; creation
 * stays dashboard-only.
 */

import React, { useEffect, useMemo, useState } from 'react'
import { FaSearch, FaSpinner } from 'react-icons/fa'
import { FaWandMagicSparkles } from 'react-icons/fa6'
import { useOnboardingStore } from '../onboardingStore'
import { fetchEffectCatalog, type CatalogEffect } from '../onboardingApi'
import SelectableCard from '../components/SelectableCard'
import { useCopy } from '../regionCopy'

interface StepEffectsProps {
  signedIn: boolean
  onRequestSignIn: () => void
}

export const StepEffects: React.FC<StepEffectsProps> = ({ signedIn, onRequestSignIn }) => {
  const aiEffectId = useOnboardingStore((s) => s.aiEffectId)
  const setAiEffectId = useOnboardingStore((s) => s.setAiEffectId)

  const [effects, setEffects] = useState<CatalogEffect[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [query, setQuery] = useState('')
  const c = useCopy()

  useEffect(() => {
    if (!signedIn) return
    let cancelled = false
    setLoading(true)
    fetchEffectCatalog()
      .then((items) => {
        if (!cancelled) setEffects(items)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [signedIn])

  const q = query.trim().toLowerCase()
  const filtered = useMemo(
    () => (effects ?? []).filter((e) => !q || e.title.toLowerCase().includes(q)),
    [effects, q]
  )

  return (
    <div className="mx-auto flex h-full w-full max-w-3xl flex-col gap-4 px-6 py-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">{c.aiEffect}</h2>
          <p className="text-sm text-whiteout/50">{c.aiEffectSub}</p>
        </div>
        {signedIn && (
          <label className="relative w-full sm:w-auto">
            <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-whiteout/30" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={c.searchEffects}
              className="w-full rounded-full border border-white/15 bg-white/[0.04] py-2 pl-8 pr-3 text-xs text-whiteout placeholder-whiteout/30 outline-none focus:border-blueSparkle sm:w-52"
            />
          </label>
        )}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto soft-scrollbar pb-4 pr-1">
        {!signedIn ? (
          <div className="flex flex-col items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-sm text-whiteout/60">
            <p>
              <FaWandMagicSparkles className="mr-1.5 inline text-blueSparkle" />
              Sign in to browse community AI effects — your booth setup stays right here.
            </p>
            <button
              type="button"
              onClick={onRequestSignIn}
              className="rounded-full bg-blueSparkle px-5 py-2 text-xs font-semibold text-whiteout"
            >
              Continue with Google
            </button>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-16 text-whiteout/50">
            <FaSpinner className="animate-spin text-2xl" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="py-12 text-center text-sm text-whiteout/40">
            {q ? `No effects match “${query}”.` : 'No public effects available yet.'}
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {filtered.map((e) => (
              <SelectableCard
                key={e.id}
                selected={aiEffectId === e.id}
                onClick={() => setAiEffectId(aiEffectId === e.id ? null : e.id)}
                ariaLabel={e.title}
                className="bg-white/[0.04] p-0"
              >
                <div className="ai-card-media relative flex aspect-square w-full items-center justify-center overflow-hidden bg-black/30">
                  {e.previewAfterUrl ? (
                    <>
                      <img
                        src={e.previewAfterUrl}
                        alt={e.title}
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                      {/* Hover: the "before" fades in, holds, slides away —
                          the same reveal the dashboard AI-effect cards use. */}
                      {e.previewBeforeUrl && (
                        <img
                          src={e.previewBeforeUrl}
                          alt=""
                          aria-hidden
                          loading="lazy"
                          className="ai-card-before-reveal absolute inset-0 h-full w-full object-cover"
                        />
                      )}
                    </>
                  ) : (
                    <FaWandMagicSparkles className="text-2xl text-whiteout/30" />
                  )}
                </div>
                <div className="px-2 py-1.5">
                  <div className="truncate text-[11px] font-medium text-whiteout/80">{e.title}</div>
                  <div className="truncate text-[10px] text-whiteout/40">{e.ownerLabel}</div>
                </div>
              </SelectableCard>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default StepEffects
